import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { STATUS_META, fmtEUR, fmtDateTime, SOURCE_LABELS } from "@/lib/constants";

const ACTION_LABELS = {
  "vehicle.created": "Fahrzeug angelegt",
  "vehicle.updated": "Daten geändert",
  "vehicle.status_changed": "Status geändert",
  "vehicle.sold": "Als verkauft markiert",
  "photo.uploaded": "Foto hochgeladen",
  "photo.deleted": "Foto gelöscht",
  "photo.processed": "Bild verarbeitet",
  "note.created": "Notiz erfasst",
  "note.applied": "KI-Extraktion übernommen",
  "claim.added": "Angabe hinzugefügt",
  "claim.confirmed": "Angabe bestätigt",
  "claim.rejected": "Angabe abgelehnt",
  "conflict.detected": "Datenkonflikt erkannt",
  "conflict.resolved": "Datenkonflikt gelöst",
  "description.generated": "KI-Beschreibung erstellt",
  "description.edited": "Beschreibung bearbeitet",
  "market.analyzed": "Marktanalyse durchgeführt",
  "price.changed": "Preis geändert",
  "publish.executed": "Veröffentlichung ausgeführt",
  "publish.failed": "Publikationsfehler",
  "publish.unpublished": "Inserat entfernt",
  "publish.synced": "Kanal synchronisiert",
  "export.csv": "CSV-Export",
  "export.json": "JSON-Export",
  "task.created": "Aufgabe erstellt",
  "task.updated": "Aufgabe aktualisiert",
  "damage.added": "Schaden dokumentiert",
  "costs.updated": "Kalkulation geändert",
};

export default function HistoryTab({ vehicle }) {
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    api.get("/audit", { params: { vehicle_id: vehicle.id, limit: 100 } }).then((r) => setAudit(r.data));
  }, [vehicle.id]);

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <Card className="lg:col-span-7" data-testid="audit-timeline">
        <CardHeader className="pb-2"><CardTitle className="text-base font-display">Audit-Historie</CardTitle></CardHeader>
        <CardContent>
          {!audit ? <p className="text-sm text-muted-foreground">Wird geladen...</p> :
            audit.length === 0 ? <p className="text-sm text-muted-foreground">Keine Einträge.</p> : (
              <div className="relative pl-5 space-y-4 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-border">
                {audit.map((a) => (
                  <div key={a.id} className="relative">
                    <span className="absolute -left-5 top-1 h-3 w-3 rounded-full border-2 border-primary bg-background" />
                    <div className="text-sm font-medium">{ACTION_LABELS[a.action] || a.action}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)} · {a.actor_name}</div>
                    {a.details && Object.keys(a.details).length > 0 && (
                      <div className="text-xs text-muted-foreground/80 mt-0.5 font-mono truncate">
                        {Object.entries(a.details).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 60) : v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      <div className="lg:col-span-5 space-y-4">
        <Card data-testid="status-history">
          <CardHeader className="pb-2"><CardTitle className="text-base font-display">Statusverlauf</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[...(vehicle.status_history || [])].reverse().map((h, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>{STATUS_META[h.to]?.label || h.to}</span>
                <span className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.by}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card data-testid="price-history">
          <CardHeader className="pb-2"><CardTitle className="text-base font-display">Preisverlauf</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(vehicle.price_history || []).length === 0 && <p className="text-sm text-muted-foreground">Noch keine Preisänderungen.</p>}
            {[...(vehicle.price_history || [])].reverse().map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="tabular-nums font-medium">{fmtEUR(p.price)}</span>
                <span className="text-xs text-muted-foreground">{SOURCE_LABELS[p.source] || p.source} · {fmtDateTime(p.at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
